import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Loader, Message } from "../../components";
import { AuthService } from "../../_service";
import { Constant } from "../../config";
import { TrustedDevice } from "../../utils";
import { useCountdown, formatCountdown } from "../../hooks";
import AccountLockedNotice from "./AccountLockedNotice";

import {
  BackToHomeLink,
  BackToHomeLinkWrap,
  Container,
  ContainerBox,
  InputFieldWrapper,
  OtpDigit,
  OtpEmail,
  OtpFooter,
  OtpInputRow,
  OtpLinkButton,
  OtpSubHeading,
  OtpTimerText,
  OtpTrustLabel,
  Title,
} from "./AuthStyles";

export interface OtpChallenge {
  /** Email the OTP was sent to - also identifies the pending login server-side. */
  email: string;
  /** Minutes the code stays valid (from the login response). */
  otp_expiry_minutes?: number;
  /** Minimum wait between resend requests (from the login response). */
  resend_cooldown_seconds?: number;
  /** Number of digits in the code; backend default is 6. */
  otp_length?: number;
  /** Only sent by the token-based backend variant; forwarded back when present. */
  verification_token?: string;
  /** Message returned by the login endpoint ("An OTP has been sent..."). */
  message?: string;
}

interface OtpVerificationProps {
  challenge: OtpChallenge;
  isEmployee: boolean;
  /** Called with the full login payload once the OTP is accepted. */
  onVerified: (data: any) => void;
  /** Called when the user abandons the OTP step and goes back to the login form. */
  onCancel: () => void;
}

const DEFAULT_OTP_LENGTH = 6;
const DEFAULT_EXPIRY_MINUTES = 5;
const DEFAULT_COOLDOWN_SECONDS = 60;

const DEV_OTP_LENGTH = 8;

/** Pulls a readable error out of the various shapes the API can return. */
const readApiError = (error: any, fallback: string) => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") return data;
  return data.error || data.message || data.detail || fallback;
};

const OtpVerification = (props: OtpVerificationProps) => {
  const { challenge, isEmployee, onVerified, onCancel } = props;

  const challengeOtpLength = challenge.otp_length || DEFAULT_OTP_LENGTH;
  const expiryMinutes = challenge.otp_expiry_minutes ?? DEFAULT_EXPIRY_MINUTES;
  const cooldownSeconds =
    challenge.resend_cooldown_seconds ?? DEFAULT_COOLDOWN_SECONDS;

  const [otpLength, setOtpLength] = useState(challengeOtpLength);
  const [digits, setDigits] = useState<string[]>(
    Array(challengeOtpLength).fill("") as string[]
  );
  const [sending, setSending] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  // Epoch ms the lockout lifts at, taken from `retry_after_minutes`.
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  // Both timers are held as deadlines rather than tick counters so they stay
  // honest when the browser throttles timers in a background tab.
  const [expiresAt, setExpiresAt] = useState(
    () => Date.now() + expiryMinutes * 60 * 1000
  );
  const [resendAt, setResendAt] = useState(
    () => Date.now() + cooldownSeconds * 1000
  );
  const [message, setMessage] = useState<{ message: string; type: string }>({
    message: challenge.message || Constant.otp.otp_sent,
    type: "success",
  });

  const lockRemaining = useCountdown(lockedUntil);
  const expiresIn = useCountdown(expiresAt);
  const resendIn = useCountdown(resendAt);
  // The lockout notice stays up once the wait is over (it says so), but the
  // screen itself reopens - the user still needs a fresh code to continue.
  const isLocked = lockRemaining > 0;

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  // Guards the auto-submit so a single filled code is only ever sent once.
  const submittingRef = useRef(false);

  const code = useMemo(() => digits.join(""), [digits]);
  const isExpired = expiresIn <= 0;
  const canVerify = !sending && !isLocked && !isExpired;
  const canResend = !sending && !isLocked && resendIn <= 0;

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (isExpired && !isLocked) {
      setMessage({ message: Constant.otp.code_expired, type: "error" });
    }
  }, [isExpired, isLocked]);

  // Ctrl+Alt+D toggles the dev-only longer code length. Bound on the document
  // so it works wherever the focus happens to sit on this screen.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.ctrlKey || !event.altKey || event.metaKey) return;
      // `key` is unreliable with Alt held on some layouts - `code` is not.
      if (event.code !== "KeyD" && event.key?.toLowerCase() !== "d") return;

      event.preventDefault();
      setOtpLength((current) =>
        current === DEV_OTP_LENGTH ? challengeOtpLength : DEV_OTP_LENGTH
      );
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [challengeOtpLength]);

  // Resize the code to match the active length, keeping whatever fits so a
  // partly typed code survives the switch.
  useEffect(() => {
    setDigits((current) =>
      Array.from({ length: otpLength }, (_, i) => current[i] ?? "")
    );
    inputRefs.current = inputRefs.current.slice(0, otpLength);
  }, [otpLength]);

  const resetCode = (focusFirst = true) => {
    setDigits(Array(otpLength).fill("") as string[]);
    if (focusFirst) inputRefs.current[0]?.focus();
  };

  const handleVerify = async (submittedCode?: string) => {
    const otp = (submittedCode ?? code).trim();

    if (otp.length !== otpLength) {
      setMessage({ message: Constant.otp.incomplete_code, type: "error" });
      return;
    }
    if (!canVerify || submittingRef.current) return;

    submittingRef.current = true;
    setSending(true);

    const payload: any = {
      email: challenge.email,
      otp,
      trust_device: trustDevice,
    };
    if (trustDevice) payload.device_name = TrustedDevice.getDeviceName();
    if (challenge.verification_token) {
      payload.verification_token = challenge.verification_token;
    }

    try {
      const res = isEmployee
        ? await AuthService._employeeVerifyOtp(payload)
        : await AuthService._verifyOtp(payload);

      if (res.status === 200 && res.data?.user?.email) {
        // Persist the trusted-device token so the next login can skip OTP.
        if (res.data.device_token) {
          TrustedDevice.setDeviceToken(challenge.email, res.data.device_token);
        }
        onVerified(res.data);
        return;
      }

      setMessage({ message: Constant.otp.verify_failed, type: "error" });
      resetCode();
    } catch (e: any) {
      const data = e?.response?.data || {};
      // Lockout responses carry retry_after_minutes - the code is dead, so the
      // screen closes down until the wait is over, then reopens for a resend.
      // The lockout notice does the explaining, so the error banner stays out
      // of its way.
      const isLockout = data.retry_after_minutes !== undefined;

      setMessage({
        message: isLockout ? "" : readApiError(e, Constant.otp.verify_failed),
        type: "error",
      });

      if (isLockout) {
        setLockedUntil(Date.now() + Number(data.retry_after_minutes) * 60 * 1000);
        setExpiresAt(Date.now());
      }
      resetCode(!isLockout);
    } finally {
      submittingRef.current = false;
      setSending(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    setSending(true);
    const payload: any = { email: challenge.email };
    if (challenge.verification_token) {
      payload.verification_token = challenge.verification_token;
    }

    try {
      const res = isEmployee
        ? await AuthService._employeeResendOtp(payload)
        : await AuthService._resendOtp(payload);

      setMessage({
        message: res.data?.message || Constant.otp.otp_resent,
        type: "success",
      });
      // A fresh code supersedes any lockout the user has already sat out.
      setLockedUntil(null);
      setResendAt(
        Date.now() +
          (res.data?.resend_cooldown_seconds ?? cooldownSeconds) * 1000
      );
      setExpiresAt(
        Date.now() +
          (res.data?.otp_expiry_minutes ?? expiryMinutes) * 60 * 1000
      );
      resetCode();
    } catch (e: any) {
      const data = e?.response?.data || {};
      const isLockout = data.retry_after_minutes !== undefined;

      setMessage({
        message: isLockout ? "" : readApiError(e, Constant.otp.resend_failed),
        type: "error",
      });

      // Honour the cooldown / lockout the backend reports back.
      if (data.retry_after_seconds !== undefined) {
        setResendAt(Date.now() + Number(data.retry_after_seconds) * 1000);
      }
      if (isLockout) {
        setLockedUntil(Date.now() + Number(data.retry_after_minutes) * 60 * 1000);
        setExpiresAt(Date.now());
      }
    } finally {
      setSending(false);
    }
  };

  const applyDigits = (next: string[]) => {
    setDigits(next);
    const joined = next.join("");
    // Auto-submit as soon as the full code is present - standard OTP behaviour.
    if (joined.length === otpLength && next.every((d) => d !== "")) {
      handleVerify(joined);
    }
  };

  const handleDigitChange = (index: number, rawValue: string) => {
    const value = rawValue.replace(/\D/g, "");
    if (!value) {
      const next = [...digits];
      next[index] = "";
      setDigits(next);
      return;
    }

    // Typing (or autofill) can deliver several digits at once - spread them.
    const next = [...digits];
    let cursor = index;
    for (const char of value.split("")) {
      if (cursor >= otpLength) break;
      next[cursor] = char;
      cursor += 1;
    }
    inputRefs.current[Math.min(cursor, otpLength - 1)]?.focus();
    applyDigits(next);
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      const next = [...digits];
      if (next[index]) {
        next[index] = "";
      } else if (index > 0) {
        next[index - 1] = "";
        inputRefs.current[index - 1]?.focus();
      }
      setDigits(next);
    } else if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (event.key === "ArrowRight" && index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus();
    } else if (event.key === "Enter") {
      handleVerify();
    }
  };

  const handlePaste = (
    index: number,
    event: React.ClipboardEvent<HTMLInputElement>
  ) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    event.preventDefault();

    const next = [...digits];
    let cursor = index;
    for (const char of pasted.split("")) {
      if (cursor >= otpLength) break;
      next[cursor] = char;
      cursor += 1;
    }
    inputRefs.current[Math.min(cursor, otpLength - 1)]?.focus();
    applyDigits(next);
  };

  return (
    <>
      {sending && <Loader />}
      <Container>
        <ContainerBox>
          <Title>{Constant.otp.title}</Title>
          <InputFieldWrapper>
            <Message
              data={message}
              close={() => setMessage({ message: "", type: "error" })}
            />

            {lockedUntil !== null && (
              <AccountLockedNotice
                remaining={lockRemaining}
                reason={Constant.lockout.otp_reason}
              />
            )}

            <OtpSubHeading>
              {Constant.otp.subHeading}{" "}
              <OtpEmail>{challenge.email}</OtpEmail>
            </OtpSubHeading>

            <OtpInputRow>
              {digits.map((digit, index) => (
                <OtpDigit
                  key={index}
                  ref={(el: HTMLInputElement | null) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                  maxLength={otpLength}
                  value={digit}
                  hasError={message.type === "error" && !!message.message}
                  disabled={isLocked}
                  aria-label={`${Constant.otp.code_label} digit ${index + 1}`}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={(e) => handlePaste(index, e)}
                  onFocus={(e) => e.target.select()}
                />
              ))}
            </OtpInputRow>

            {!isLocked && (
              <OtpTimerText expired={isExpired}>
                {isExpired
                  ? Constant.otp.code_expired
                  : `${Constant.otp.code_expires_in} ${formatCountdown(
                      expiresIn
                    )}`}
              </OtpTimerText>
            )}

            {!isLocked && (
              <OtpTrustLabel>
                <input
                  type="checkbox"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                />
                <span>{Constant.otp.trust_device}</span>
              </OtpTrustLabel>
            )}

            {!isLocked && (
              <Button title={Constant.otp.verify} onClick={() => handleVerify()} />
            )}

            <OtpFooter>
              {!isLocked && (
                <>
                  <span>{Constant.otp.didnt_receive}</span>
                  <OtpLinkButton disabled={!canResend} onClick={handleResend}>
                    {resendIn > 0
                      ? `${Constant.otp.resend_in} ${formatCountdown(resendIn)}`
                      : Constant.otp.resend}
                  </OtpLinkButton>
                </>
              )}
            </OtpFooter>

            <OtpFooter>
              <OtpLinkButton onClick={onCancel}>
                {Constant.otp.use_another_account}
              </OtpLinkButton>
            </OtpFooter>
          </InputFieldWrapper>

          <BackToHomeLinkWrap>
            <BackToHomeLink to="https://tellboss.co/">
              {Constant.common.back_to_home_page}
            </BackToHomeLink>
          </BackToHomeLinkWrap>
        </ContainerBox>
      </Container>
    </>
  );
};

export default OtpVerification;
